import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {RouterModule} from '@angular/router'; // ✅ Import RouterModule
import {FormsModule} from '@angular/forms'; // Keep if you're using ngModel
import {
    DsBreadcrumbModule,
    DsButtonModule,
    DsCheckboxModule,
    DsColumnData,
    DsFormFieldModule,
    DsIconModule,
    DsLoadingAreaConfiguration,
    DsLoadingAreaModule,
    DsLoadingAreaService,
    DsLoadingModule,
    DsProgressCircleModule,
    DsRowData,
    DsSegmentedControlModule,
    DsSelectModule,
    DsTableConfig,
    DsTableModule,
    DsTabsModule,
    SelectListOption,
} from '@bmw-ds/components';
import {BreadcrumbComponent} from 'src/app/components/breadcrumb/breadcrumb.component';
import {CriteriaService} from 'src/app/services/criteria.service';
import {CriteriaResponse} from 'src/app/models/criteria.model';
import {CommonModule} from '@angular/common';
import {CriteriaStoreService} from '../../services/criteria-store.service';
import {ReplenishmentService} from '../../services/replenishment.service';
import {
    ReplenishCapacity,
    ReplenishCapacityResponse,
    ReplenishmentCapacityRequest,
} from '../../models/showroom-capacity.model';
import {firstValueFrom, take} from 'rxjs';
import {CosyUrlResponse} from '../../models/cosy-response.model';
import {StockService} from '../../services/stock.service';
import {AuthService} from '../../services/auth.service';
import {COMMON_IMPORTS} from "../../shared/common-imports";

@Component({
    selector: 'app-showroom-capacity',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        DsTabsModule,
        FormsModule,
        DsBreadcrumbModule,
        DsTableModule,
        DsSelectModule,
        DsFormFieldModule,
        DsCheckboxModule,
        DsProgressCircleModule,
        BreadcrumbComponent,
        DsLoadingAreaModule,
        DsButtonModule,
        DsLoadingModule,
        DsSegmentedControlModule,
        DsIconModule,
        COMMON_IMPORTS
    ],
    templateUrl: './showroom-capacity.component.html',
    styleUrl: './showroom-capacity.component.scss',
})
export class ShowroomCapacityComponent implements OnInit {
    criteriaData!: CriteriaResponse;
    requestPayload!: ReplenishmentCapacityRequest;
    response!: ReplenishCapacityResponse;
    loadingAreaConfig: DsLoadingAreaConfiguration = {id: 'bodyLoading'};
    brandOptions: { id: string; label: string }[] = [];
    storageLocationOptions: SelectListOption[] = [
        { id: 'NSCO', label: 'NSCOU' },
        { id: 'BASE', label: 'BASEMENT' },
    ];
    rowsData: DsRowData[] = [];
    filters = {
        brand: '',
        storageLocation: '',
    };
    config: Partial<DsTableConfig> = {
        hasColumnDivider: true,
        hasStickyHeader: true,
        hasZebraRows: true,
        hasHeaderRowDivider: true,
        hasHeader: true,
        hasOuterDivider: true,
    };

    columnData: DsColumnData[] = [
        { field: 'image', headerName: 'Image' },
        { field: 'dealerActuals', headerName: 'VIN' }, // Rename for clarity
        { field: 'status', headerName: 'Status' },
        { field: 'unitSalesOrder', headerName: 'In Sales Order' },
    ];

    showResults: boolean = false;

    constructor(
        private readonly criteriaService: CriteriaService,
        private readonly criteriaStoreService: CriteriaStoreService,
        private readonly stockService: StockService,
        private readonly loadingAreaService: DsLoadingAreaService,
        private readonly replenishmentService: ReplenishmentService,
        private readonly cdr: ChangeDetectorRef,
        private readonly authService: AuthService
    ) {
    }

    ngOnInit(): void {
        this.authService.isAuthenticated().pipe(take(1)).subscribe((isAuthenticated) => {
            if (isAuthenticated) {
                this.loadCriteriaData(localStorage.getItem('dealerCode') ?? '');
            } else {
                window.location.href = this.authService.getAuthUrl(); // Redirect to the auth URL
            }
        });
    }

    async loadCriteriaData(dealerCode: string): Promise<void> {
        this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
        try {
            const data: CriteriaResponse = await firstValueFrom(
                this.criteriaService.postCriteriaSelection(dealerCode)
            );
            this.criteriaData = data;
            this.cdr.detectChanges();
            this.criteriaStoreService.setCriteriaData(data);
            this.brandOptions = data.dealerBrands.map((brand) => ({
                id: brand.brandCode,
                label: brand.brandDescription,
            }));
        } catch (error) {
            console.error('Error fetching criteria data', error);
        } finally {
            this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
        }
    }

    callReplishCapacity() {
        if (!this.isSearchEnabled) {
            return;
        }

        this.loadingAreaConfig = {
            id: 'bodyLoading',
            loadingTitle: 'Loading',
            loadingText: 'Replenishment Capacity',
        };
        this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
        this.requestPayload = {
            plant: this.criteriaData.plant,
            brand: this.filters.brand,
            vehicleType:
                this.getVehicleTypeForSelectedBrand(this.filters.brand) ?? '',
            storageLocation: this.filters.storageLocation,
        };

        this.replenishmentService
            .getStockReplenishmentCapacity(this.requestPayload)
            .subscribe({
                next: (response: ReplenishCapacityResponse) => {
                    this.response = response;
                    this.rowsData = this.normalizeData(response?.replenishCapacityList || []) ;
                    this.cdr.detectChanges();
                },
                error: (error: unknown) => {
                    console.error('Error fetching replenishment capacity', error);
                },
                complete: () => {
                    this.showResults = true;
                    this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
                    this.rowsData.forEach((row, idx) => {
                        const cosyRequest = {VIN: row['dealerActuals']};
                        this.stockService
                            .getTableCosyUrl(cosyRequest)
                            .subscribe((response: CosyUrlResponse) => {
                                this.rowsData = this.rowsData.map((r, i) =>
                                    i === idx ? {...r, image: response.exturl} : r
                                );
                                this.cdr.detectChanges();
                            });
                    });
                },
            });
    }

    getStatus(item: ReplenishCapacity): string {
        const statusArr: string[] = [];

        if (typeof item.available === 'string' && item.available.trim() !== '' && item.actual && item.available.includes(item.actual)) {
            statusArr.push('Available');
        }
        if (typeof item.pending === 'string' && item.pending.trim() !== '' && item.actual && item.pending.includes(item.actual)) {
            statusArr.push('Pending');
        }
        if (typeof item.accepted === 'string' && item.accepted.trim() !== '' && item.actual && item.accepted.includes(item.actual)) {
            statusArr.push('Accepted');
        }

        if (statusArr.length === 0) {
            statusArr.push('No Status');
        }

        return statusArr.join(' ');
    }


    getVehicleTypeForSelectedBrand(
        selectedBrandCode: string
    ): string | undefined {
        const brand = this.criteriaData?.dealerBrands.find(
            (b) => b.brandCode === selectedBrandCode
        );
        return brand?.vehicleType;
    }

    private vinExistsInSoUnits(vin?: string, soUnits?: string): boolean {
        if (!soUnits || !vin) return false;
        const vinList = soUnits.split(',').map((v) => v.trim());
        return vinList.includes(vin);
    }

    get isSearchEnabled(): boolean {
        return !!this.filters.brand && !!this.filters.storageLocation;
    }
    private normalizeData(list: ReplenishCapacity[]): DsRowData[] {
        const vinMap = new Map<string, DsRowData>();

        const addOrUpdateVin = (vin: string, category: string, item: ReplenishCapacity) => {
            if (!vin || vin.trim() === '') return;

            const existing = vinMap.get(vin);
            if (existing) {
                if (!existing['status'] || existing['status'].toString().trim() === '') {
                    existing['status'] = category;
                } else if (!existing['status'].toString().includes(category)) {
                    existing['status'] += `, ${category}`;
                }
            } else {
                vinMap.set(vin, {
                    image: '',
                    dealerActuals: vin,
                    status: category,
                    unitSalesOrder: this.vinExistsInSoUnits(vin, item.soUnits) ? 'Yes' : 'No',
                });
            }
        };

        list.forEach(item => {
            // Exclude Actual, only add these categories:
            if (item.pending) addOrUpdateVin(item.pending, 'Pending', item);
            if (item.available) addOrUpdateVin(item.available, 'Available', item);
            if (item.accepted) addOrUpdateVin(item.accepted, 'Accepted', item);

            if (item.soUnits) {
                const soVins = item.soUnits.split(',').map(v => v.trim());
                soVins.forEach(vin => addOrUpdateVin(vin, 'SO Units', item));
            }
        });

        return Array.from(vinMap.values());
    }

    private getStatusForVin(item: ReplenishCapacity, vin: string): string {
        const statusArr: string[] = [];
        if (item.available && item.available.includes(vin)) statusArr.push('Available');
        if (item.pending && item.pending.includes(vin)) statusArr.push('Pending');
        if (item.accepted && item.accepted.includes(vin)) statusArr.push('Accepted');
        if (statusArr.length === 0) statusArr.push('No Status');
        return statusArr.join(' ');
    }
}
